import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, ArrowRight, Newspaper } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import Layout from "@/components/Layout";
import ArticleCard from "@/components/ArticleCard";
import SignalWidget from "@/components/SignalWidget";
import AdSlot from "@/components/AdSlot";
import NewsletterBox from "@/components/NewsletterBox";
import RecommendedForYou from "@/components/RecommendedForYou";
import SocialLinks from "@/components/SocialLinks";
import { useCategories } from "@/context/CategoriesContext";
import { paletteFor } from "@/lib/categories";

export default function Home() {
  const { data: featured, isLoading: fLoad } = useQuery({
    queryKey: ["featured"],
    queryFn: () => api.get("/articles/featured").then((r) => r.data),
  });
  const { data: stream } = useQuery({
    queryKey: ["stream"],
    queryFn: () => api.get("/articles?limit=40").then((r) => r.data),
  });
  const { data: mostRead } = useQuery({
    queryKey: ["most-read"],
    queryFn: () => api.get("/articles/most-read").then((r) => r.data),
  });

  const LATEST_PAGE = 8;
  const {
    data: latestData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["home-latest"],
    queryFn: ({ pageParam = 0 }) =>
      api.get(`/articles?limit=${LATEST_PAGE}&skip=${pageParam}`).then((r) => r.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const { categories } = useCategories();

  const items = stream?.items || [];
  const latestItems = latestData?.pages.flatMap((p) => p.items) || [];

  if (fLoad) {
    return (
      <Layout>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Подуяне Днес",
      url: origin,
      inLanguage: "bg-BG",
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/category/all?search={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "NewsMediaOrganization",
      name: "Подуяне Днес",
      url: origin,
      description: "Независима хиперлокална медия за район Подуяне, София.",
    },
  ];

  return (
    <Layout>
      <Seo jsonLd={homeJsonLd} />
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <AdSlot placement="home_top" className="mb-8" />
        {/* HERO */}
        <section className="grid gap-6 lg:grid-cols-12" data-testid="hero-section">
          <div className="lg:col-span-8">
            {featured?.lead && <ArticleCard article={featured.lead} variant="hero" />}
          </div>
          <div className="flex flex-col gap-5 lg:col-span-4">
            {(featured?.secondary || []).slice(0, 4).map((a) => (
              <ArticleCard key={a.id} article={a} variant="horizontal" />
            ))}
          </div>
        </section>

        {/* STREAM + SIDEBAR */}
        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <SectionHeading icon={Newspaper} title="Последни новини" />
            <div className="grid gap-6 sm:grid-cols-2" data-testid="news-stream">
              {latestItems.map((a) => (
                <ArticleCard key={a.id} article={a} variant="default" />
              ))}
            </div>
            {hasNextPage && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  data-testid="home-load-more-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
                >
                  {isFetchingNextPage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                  Зареди още новини
                </button>
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-8 lg:col-span-4">
            <SignalWidget />
            <SocialLinks variant="block" />
            <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="most-read-box">
              <SectionHeading icon={TrendingUp} title="Най-четено" small />
              <div className="mt-1 divide-y divide-slate-100">
                {(mostRead || []).map((a, i) => (
                  <ArticleCard key={a.id} article={a} variant="text" index={i + 1} />
                ))}
              </div>
            </div>
            <AdSlot placement="sidebar" />
          </aside>
        </div>

        <RecommendedForYou requireHistory className="mt-14" />

        <AdSlot placement="home_infeed" className="mt-12" />

        {/* CATEGORY BLOCKS */}
        {categories.map((cat) => {
          const list = items.filter((a) => a.category === cat.name).slice(0, 3);
          if (list.length === 0) return null;
          const meta = paletteFor(cat.color);
          return (
            <section key={cat.id || cat.slug} className="mt-14" data-testid={`category-block-${cat.slug}`}>
              <div className="mb-5 flex items-center justify-between border-b border-slate-200 pb-3">
                <div className="flex items-center gap-2.5">
                  <span className={`h-6 w-1.5 rounded-full ${meta.solid}`} />
                  <h2 className="font-display text-2xl font-extrabold text-slate-900">
                    {cat.name}
                  </h2>
                </div>
                <Link
                  to={`/category/${cat.slug}`}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  data-testid={`category-block-more-${cat.slug}`}
                >
                  Всички
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((a) => (
                  <ArticleCard key={a.id} article={a} variant="default" />
                ))}
              </div>
            </section>
          );
        })}

        <div className="mt-16">
          <NewsletterBox />
        </div>
      </div>
    </Layout>
  );
}

function SectionHeading({ icon: Icon, title, small }) {
  return (
    <div className="mb-5 flex items-center gap-2">
      <Icon className={small ? "h-4 w-4 text-primary" : "h-5 w-5 text-primary"} />
      <h2
        className={`font-display font-extrabold text-slate-900 ${
          small ? "text-base uppercase tracking-wide" : "text-xl"
        }`}
      >
        {title}
      </h2>
    </div>
  );
}
